import type { AllowedInContext } from '@/state/queries/composite-components/use-get-composite-components';
import type { FlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';

type CompositeApplicability = {
  flowScopes?: string[];
  sessionTypes?: string[];
  slotContentTypes?: string[];
  eventTypes?: string[];
};

export type FlowPlaylistVirtualContext = {
  parentType: 'flow' | 'session';
  scope: FlowPlaylistScope | 'session';
  slotContentType?: string;
  sessionType?: string;
};

/** Pre-applicability records: empty allowedIn with no applicability object configured. */
export function isLegacyUnrestrictedComposite(
  allowedIn: AllowedInContext[] | undefined,
  applicability: CompositeApplicability | undefined,
): boolean {
  return Array.isArray(allowedIn) && allowedIn.length === 0 && (applicability === undefined || applicability === null);
}

function matchesStringList(values: string[] | undefined, value: string | undefined): boolean {
  if (!values?.length) {
    return true;
  }
  if (!value) {
    return false;
  }
  return values.includes(value);
}

function isSlotPlaylistScope(scope: FlowPlaylistScope | 'session'): scope is 'slot' {
  return scope === 'slot';
}

function matchesAllowedIn(
  allowedIn: AllowedInContext[] | undefined,
  applicability: CompositeApplicability | undefined,
  ctx: FlowPlaylistVirtualContext,
): boolean {
  if (allowedIn === undefined || allowedIn === null) {
    return true;
  }
  if (allowedIn.length === 0) {
    return isLegacyUnrestrictedComposite(allowedIn, applicability);
  }
  if (ctx.parentType === 'session') {
    return allowedIn.includes('session');
  }
  if (isSlotPlaylistScope(ctx.scope)) {
    return allowedIn.includes('break') || allowedIn.includes('flow');
  }
  return allowedIn.includes('flow');
}

function matchesApplicability(
  applicability: CompositeApplicability | undefined,
  ctx: FlowPlaylistVirtualContext,
): boolean {
  if (!applicability) {
    return true;
  }
  if (ctx.parentType === 'session') {
    if (applicability.flowScopes?.length) {
      return false;
    }
    if (!matchesStringList(applicability.sessionTypes, ctx.sessionType)) {
      return false;
    }
    return true;
  }
  if (isSlotPlaylistScope(ctx.scope)) {
    if (applicability.flowScopes?.length && !applicability.flowScopes.includes('slot')) {
      if (!applicability.slotContentTypes?.length) {
        return false;
      }
    }
    if (applicability.sessionTypes?.length) {
      return false;
    }
    if (!matchesStringList(applicability.slotContentTypes, ctx.slotContentType)) {
      return false;
    }
    if (applicability.flowScopes?.length && !applicability.flowScopes.includes('slot')) {
      return matchesStringList(applicability.slotContentTypes, ctx.slotContentType);
    }
    return true;
  }
  if (applicability.sessionTypes?.length) {
    return false;
  }
  if (applicability.flowScopes?.length) {
    return applicability.flowScopes.includes(ctx.scope);
  }
  if (applicability.slotContentTypes?.length) {
    return false;
  }
  return true;
}

export function compositeMatchesPlaylistContext(
  composite: {
    allowedIn?: AllowedInContext[];
    applicability?: CompositeApplicability;
  },
  ctx: FlowPlaylistVirtualContext,
): boolean {
  if (!matchesAllowedIn(composite.allowedIn, composite.applicability, ctx)) {
    return false;
  }
  return matchesApplicability(composite.applicability, ctx);
}
